import { useState, useEffect, useRef, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Heart, Activity, Brain, Bone, ChevronLeft, ChevronRight, ArrowRight, ShieldCheck, Globe, Microscope, Users, Handshake } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { authorSlug } from "@/lib/authorSlug";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { uiT } from "@/i18n/ui";
import { mdCroT } from "@/i18n/mdCro";
import { Button } from "@/components/ui/button";
import SplitBanner from "@/components/ui/SplitBanner";

// Expert images
import dianeImg from "@/assets/profiles/diane-fairweather.png";
import julieImg from "@/assets/services/clinical-solutions/experts/julie-hendrickx.png";
import petraImg from "@/assets/services/clinical-solutions/experts/petra-de-geest.png";
import sarahImg from "@/assets/services/clinical-solutions/experts/sarah-andries.png";

// Advisor images
import pierImg from "@/assets/services/clinical-solutions/advisors/pierfrancesco-agostoni.jpg";
import lievenImg from "@/assets/services/clinical-solutions/advisors/lieven-lagae.jpg";
import djordjeImg from "@/assets/services/clinical-solutions/advisors/djordje-lazovic.jpg";

// Case images
import vascularImg from "@/assets/services/clinical-solutions/cases/vascular-samd.jpg";
import cardioImg from "@/assets/services/clinical-solutions/cases/cardio-heart-failure.jpg";
import neuroImg from "@/assets/services/clinical-solutions/cases/neuro-stroke.jpg";
import orthoImg from "@/assets/services/clinical-solutions/cases/ortho-osteoarthritis.jpg";

// Image break photos
import imageBreak1 from "@/assets/services/clinical-solutions/md-cro-image-break-1.png";
import imageBreak2 from "@/assets/services/clinical-solutions/md-cro-image-break-2.png";

const COLOR = "#FFC60C";

const anim = (i: number) => ({
  initial: { opacity: 0, y: 16 } as const,
  whileInView: { opacity: 1, y: 0 } as const,
  viewport: { once: true } as const,
  transition: { duration: 0.4, delay: i * 0.08 },
});

interface Props {
  onCtaClick: () => void;
  offeringsSlot?: React.ReactNode;
}

/* ── Horizontal person card (used for experts & advisors) ── */
const PersonCard = ({
  person,
  index,
  onCtaClick,
  showCta = false,
  bgClass = "bg-muted/30",
}: {
  person: { name: string; role: string; experience?: string; bullets: string[]; image: string };
  index: number;
  onCtaClick?: () => void;
  showCta?: boolean;
  bgClass?: string;
}) => {
  const lang = useLanguage();
  const pre = langPrefix(lang);
  return (
  <motion.div
    className={`flex gap-4 items-start ${bgClass} rounded-xl p-5 border border-border/40`}
    {...anim(index)}
  >
    <Link to={`${pre}/author/${authorSlug(person.name)}`} className="shrink-0">
      <img
        src={person.image}
        alt={person.name}
        className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover hover:ring-2 hover:ring-[var(--service-color)] transition-all"
        loading="lazy"
      />
    </Link>
    <div className="min-w-0">
      <Link to={`${pre}/author/${authorSlug(person.name)}`} className="hover:underline">
        <h3 className="text-base font-semibold text-foreground">{person.name}</h3>
      </Link>
      <p className="text-sm text-muted-foreground mb-1">{person.role}</p>
      {person.experience && (
        <p className="text-sm font-medium text-foreground mb-2">{person.experience}</p>
      )}
      <ul className="text-sm text-muted-foreground space-y-1">
        {person.bullets.map((b, j) => (
          <li key={j} className="flex gap-2.5">
            <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: COLOR }} />
            {b}
          </li>
        ))}
      </ul>
      {showCta && onCtaClick && (
        <button onClick={onCtaClick} className="mt-3 text-sm font-medium text-primary hover:underline">
          {uiT(lang)("getInTouchArrow")}
        </button>
      )}
    </div>
  </motion.div>
  );
};

import AnchorNav from "./AnchorNav";
import GlobalCoverageMap from "./GlobalCoverageMap";

/* ── Person carousel (Embla) ── */
const PersonCarousel = ({
  persons,
  onCtaClick,
  showCta = false,
  bgClass = "bg-muted/30",
}: {
  persons: { name: string; role: string; experience?: string; bullets: string[]; image: string }[];
  onCtaClick?: () => void;
  showCta?: boolean;
  bgClass?: string;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false, slidesToScroll: 1 });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => { emblaApi.off("select", onSelect); emblaApi.off("reInit", onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-5 items-stretch">
          {persons.map((person, i) => (
            <div key={person.name} className="flex-[0_0_78%] sm:flex-[0_0_44%] min-w-0 flex">
              <div className="flex-1">
                <PersonCard person={person} index={i} onCtaClick={onCtaClick} showCta={showCta} bgClass={`${bgClass} h-full`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {(canPrev || canNext) && (
        <div className="flex items-center gap-2 mt-5 justify-end">
          <button
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canPrev}
            className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canNext}
            className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

const MedicalDeviceCROContent = ({ onCtaClick, offeringsSlot }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const m = mdCroT(lang);

  const anchorSections = [
    { id: "intro", label: m("anchorOverview") },
    { id: "global-coverage", label: m("coverageTitle") },
    { id: "therapeutic-areas", label: m("anchorTherapeutic") },
    { id: "experts", label: m("anchorExperts") },
    { id: "advisors", label: m("anchorAdvisors") },
    { id: "cases", label: m("anchorCases") },
  ];

  const therapeuticAreas = [
    { icon: Heart, title: m("areaCardiologyVascular"), description: m("areaCardiologyVascularDesc"), href: "/en/industries/medical-devices/md-cro-cardiology" },
    { icon: Brain, title: m("areaNeurology"), description: m("areaNeurologyDesc"), href: undefined as string | undefined },
    { icon: Bone, title: m("areaOrthopedic"), description: m("areaOrthopedicDesc"), href: "/en/industries/medical-devices/md-cro-orthopaedic-spine" },
  ];

  const experts = [
    {
      name: "Dr. Diane Fairweather",
      role: m("expertRole0"),
      experience: m("expertExp0"),
      bullets: [
        m("expert0Bullet1"),
        m("expert0Bullet2"),
        m("expert0Bullet3"),
      ],
      image: dianeImg,
    },
    {
      name: "Julie Hendrickx",
      role: m("expertRole1"),
      experience: m("expertExp1"),
      bullets: [
        m("expert1Bullet1"),
        m("expert1Bullet2"),
        m("expert1Bullet3"),
      ],
      image: julieImg,
    },
    {
      name: "Petra De Geest",
      role: m("expertRole2"),
      experience: m("expertExp2"),
      bullets: [
        m("expert2Bullet1"),
        m("expert2Bullet2"),
        m("expert2Bullet3"),
      ],
      image: petraImg,
    },
    {
      name: "Sarah Andries",
      role: m("expertRole3"),
      experience: m("expertExp3"),
      bullets: [
        m("expert3Bullet1"),
        m("expert3Bullet2"),
        m("expert3Bullet3"),
        m("expert3Bullet4"),
      ],
      image: sarahImg,
    },
  ];

  const advisors = [
    {
      name: "Pierfrancesco Agostoni",
      role: m("advisorRole1"),
      bullets: [
        m("advisor1Bullet1"),
        m("advisor1Bullet2"),
        m("advisor1Bullet3"),
        m("advisor1Bullet4"),
      ],
      image: pierImg,
    },
    {
      name: "Lieven Lagae",
      role: m("advisorRole2"),
      bullets: [
        m("advisor2Bullet1"),
        m("advisor2Bullet2"),
        m("advisor2Bullet3"),
      ],
      image: lievenImg,
    },
    {
      name: "Djordje Lazovic",
      role: m("advisorRole3"),
      bullets: [
        m("advisor3Bullet1"),
        m("advisor3Bullet2"),
        m("advisor3Bullet3"),
      ],
      image: djordjeImg,
    },
  ];

  const clientCases = [
    { area: m("case1Area"), tag: "Vascular", description: m("case1Desc"), image: vascularImg },
    { area: m("case2Area"), tag: "Cardiology", description: m("case2Desc"), image: cardioImg },
    { area: m("case3Area"), tag: "Neurology", description: m("case3Desc"), image: neuroImg },
    { area: m("case4Area"), tag: "Orthopedic", description: m("case4Desc"), image: orthoImg },
  ];

  return (
    <>
      {/* Anchor nav */}
      <AnchorNav sections={anchorSections} />

      {/* Intro */}
      <section id="intro" className="py-14 sm:py-20 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl space-y-4">
          <p className="text-base text-muted-foreground leading-relaxed [&_strong]:text-foreground" dangerouslySetInnerHTML={{ __html: m("introP1") }} />
          <p className="text-base text-muted-foreground leading-relaxed [&_strong]:text-foreground" dangerouslySetInnerHTML={{ __html: m("introP2") }} />
        </div>
      </section>

      {/* Global Coverage Map */}
      <GlobalCoverageMap />

      {/* Therapeutic Areas */}
      <section id="therapeutic-areas" className="py-16 sm:py-20 bg-muted/30 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{m("therapeuticOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{m("therapeuticTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-4 max-w-3xl [&_strong]:text-foreground" dangerouslySetInnerHTML={{ __html: m("therapeuticP1") }} />
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl [&_strong]:text-foreground" dangerouslySetInnerHTML={{ __html: m("therapeuticP2") }} />
          <div className="grid sm:grid-cols-2 gap-6">
            {therapeuticAreas.map((area, i) => {
              const Icon = area.icon;
              const inner = (
                <>
                  <Icon size={28} className="mt-0.5 shrink-0" style={{ color: COLOR }} strokeWidth={1.5} />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1.5 group-hover:text-primary transition-colors">{area.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{area.description}</p>
                  </div>
                </>
              );
              return area.href ? (
                <motion.div key={area.title} {...anim(i)}>
                  <Link
                    to={localizeHref(area.href, lang)}
                    className="group flex gap-4 items-start bg-background rounded-xl p-6 shadow-sm border border-border/40 hover:shadow-md hover:border-primary/30 transition-all h-full"
                  >
                    {inner}
                  </Link>
                </motion.div>
              ) : (
                <motion.div key={area.title} className="flex gap-4 items-start bg-background rounded-xl p-6 shadow-sm border border-border/40" {...anim(i)}>
                  {inner}
                </motion.div>
              );
            })}
          </div>
          {offeringsSlot}
        </div>
      </section>

      {/* Image break 1 */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2 items-stretch">
          <div className="relative">
            <img
              src={imageBreak1}
              alt="Medical device clinical consultation"
              className="w-full h-full object-cover min-h-[320px] lg:min-h-[420px]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-primary/10 lg:to-transparent" />
          </div>
          <div className="flex flex-col justify-center bg-primary px-8 sm:px-12 lg:px-16 py-14 sm:py-20">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-lg"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-primary-foreground mb-4">
                {m("imageBreak1Title")}
              </h2>
              <p className="text-base text-primary-foreground/75 leading-relaxed mb-6">
                {m("imageBreak1Desc")}
              </p>
              <Button
                size="lg"
                className="rounded-full px-8 shadow-lg hover:opacity-90"
                style={{ backgroundColor: COLOR, color: "#1a1a1a" }}
                onClick={onCtaClick}
              >
                {m("imageBreak1Cta")} <ArrowRight size={16} className="ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose QbD Clinical */}
      <section id="why-qbd" className="py-16 sm:py-20 bg-muted/30 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{m("whyOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            {m("whyTitle")}
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">
            {m("whySubtitle")}
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: Microscope, title: m("whyReason1Title"), description: m("whyReason1Desc") },
              { icon: ShieldCheck, title: m("whyReason2Title"), description: m("whyReason2Desc") },
              { icon: Globe, title: m("whyReason3Title"), description: m("whyReason3Desc") },
              { icon: Users, title: m("whyReason4Title"), description: m("whyReason4Desc") },
              { icon: Handshake, title: m("whyReason5Title"), description: m("whyReason5Desc") },
            ].map((reason, i) => {
              const Icon = reason.icon;
              return (
                <motion.div key={reason.title} className="flex gap-3 items-start" {...anim(i)}>
                  <Icon size={22} className="mt-0.5 shrink-0" style={{ color: COLOR }} strokeWidth={1.5} />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-1">{reason.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{reason.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Meet our experts */}
      <section id="experts" className="py-16 sm:py-20 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{m("expertsOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{m("expertsTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">
            {m("expertsSubtitle")}
          </p>
          <PersonCarousel persons={experts} onCtaClick={onCtaClick} showCta bgClass="bg-muted/30" />
        </div>
      </section>

      {/* Advisory Board */}
      <section id="advisors" className="py-16 sm:py-20 bg-muted/30 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{m("advisorsOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{m("advisorsTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">
            {m("advisorsSubtitle")}
          </p>
          <PersonCarousel persons={advisors} bgClass="bg-background" />
        </div>
      </section>

      {/* Image break 2 */}
      <SplitBanner
        title={m("imageBreak2Title")}
        description={m("imageBreak2Desc")}
        image={imageBreak2}
        imageAlt="Medical device regulatory strategy meeting"
        ctaLabel={m("imageBreak2Cta")}
        onCtaClick={onCtaClick}
        accentColor={COLOR}
      />

      {/* Client Cases */}
      <section id="cases" className="py-16 sm:py-20 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{m("casesOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{m("casesTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">
            {m("casesSubtitle")}
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {clientCases.map((c, i) => (
              <motion.div
                key={c.area}
                className="group rounded-xl overflow-hidden border border-border/40 bg-muted/30 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                {...anim(i)}
              >
                <div className="relative">
                  <img src={c.image} alt={c.area} className="w-full h-52 object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                  <span
                    className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide"
                    style={{ backgroundColor: COLOR, color: "#1a1a1a" }}
                  >
                    {c.tag}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{c.area}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.description}</p>
                  <button onClick={onCtaClick} className="mt-4 text-sm font-medium text-primary hover:underline">
                    {t("getInTouchArrow")}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default MedicalDeviceCROContent;
